import Axios from 'axios';
import jwt_decode from 'jwt-decode';
import { LoginRequest, SchoolTokenRequest } from '@/models/loginRequest';
import { UserTypes, getUserTypeFromString } from '@/enums/userTypes';
import * as AccountResponse from '@/models/accountResponse';
import school from '@/modules/admin/school';

interface State {
  loggedIn: boolean;
  pending: boolean;
  token: string | null;
  refreshToken: string | null;
  latestSchoolId: string | null;
}
const mapState: State = {
  loggedIn: !!localStorage.getItem('token'),
  pending: false,
  token: localStorage.getItem('token'),
  refreshToken: localStorage.getItem('refreshToken'),
  latestSchoolId: localStorage.getItem('latestSchoolId'),
};

const mapGetters = {
  accountId: (state: State) => {
    if (!state.token) {
      return null;
    } else {
      const decodedToken = jwt_decode(state.token);
      const findKey = Object.keys(decodedToken).find((key) => {
        return /sid$/i.test(key);
      });
      if (!findKey) {
        return null;
      } else {
        return decodedToken[findKey];
      }
    }
  },
  schoolId: (state: State) => {
    if (!state.token) {
      return null;
    } else {
      const decodedToken = jwt_decode(state.token);
      const findKey = Object.keys(decodedToken).find((key) => {
        return /schoolid$/i.test(key);
      });
      if (!findKey) {
        return null;
      } else {
        return decodedToken[findKey];
      }
    }
  },
  userType: (state: State) => {
    if (!state.token) {
      return UserTypes.All;
    } else {
      const decodedToken = jwt_decode(state.token);
      const findKey = Object.keys(decodedToken).find((key) => {
        return /role$/i.test(key);
      });
      if (!findKey) {
        return UserTypes.All;
      } else {
        return getUserTypeFromString(decodedToken[findKey]);
      }
    }
  },
};

const mapActions = {
  login({ commit }: any, data: any) {
    const schoolId = data.schoolId;
    const userId = data.userId;
    const password = data.password;

    commit('login');
    localStorage.removeItem('token');

    const request: LoginRequest = {
      grant_type: 'password',
      schoolId,
      userId,
      password,
      refreshToken: undefined,
    };
    return Axios.post('/token', request).then((response) => {
      switch (response.data.userType as UserTypes) {
        case UserTypes.Admin:
        case UserTypes.Owner:
        case UserTypes.AdminTutor:
        case UserTypes.Tutor:
          break;
        default:
          commit('login_error');
          throw new Error('講師IDまたはパスワードが正しくありません。');
      }

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      localStorage.setItem('latestSchoolId', schoolId);

      commit('login_success');
    }).catch((error) => {
      commit('login_error');
      throw new Error('講師IDまたはパスワードが正しくありません。');
    });
  },
  refreshToken({ commit }: any) {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('リフレッシュトークンが見つかりません。');
    }

    commit('login');

    const request: LoginRequest = {
      grant_type: 'refresh_token',
      refreshToken,
      schoolId: undefined,
      userId: undefined,
      password: undefined,
    };
    return Axios.post('/token', request).then((response) => {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('refreshToken', response.data.refreshToken);

      commit('login_success');
    }).catch((error) => {
      commit('login_error');
      throw new Error('トークンの取得に失敗しました。');
    });
  },
  logout({ commit }: any) {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    commit('logout');
  },
  async singleSignOn(
    { commit, state }: any,
    { token, refreshToken }: { token: string, refreshToken: string }
  ) {
    if (!token) {
      commit('login_error');
      throw new Error();
    }

    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);

    state.token = localStorage.getItem('token');
    state.refreshToken = localStorage.getItem('refreshToken');

    const response = await Axios.post('/api/Account/MyData').catch((error) => {
      commit('login_error');
      throw error;
    });

    const account: AccountResponse.TutorAccountDetailResponse = response.data;

    switch (account.authority) {
      case UserTypes.Admin:
      case UserTypes.Owner:
      case UserTypes.AdminTutor:
      case UserTypes.Tutor:
      case UserTypes.Student:
        break;
      default:
        commit('login_error');
        throw new Error();
    }

    commit('login_success');
  },
  async schoolSingleSignOn(
    { dispatch, commit, state }: any,
    { token, schoolId }: { token: string, schoolId: string }
  ) {
    if (!token || !schoolId) {
      commit('login_error');
      throw new Error('認証情報が不明です。');
    }

    localStorage.setItem('token', token);
    state.token = localStorage.getItem('token');

    try {
      await dispatch('changeSchool', schoolId);
    } catch (error) {
      commit('login_error');
      throw error;
    }
  },
  async changeSchool({ commit, state }: any, schoolId: string) {
    if (!schoolId) {
      throw new Error('スクールが不明です。');
    }

    const request: SchoolTokenRequest = {
      schoolId,
    };
    return Axios.post('/api/authenticate/schoolToken', request).then((response) => {
      switch (response.data.userType as UserTypes) {
        case UserTypes.Admin:
        case UserTypes.Owner:
          break;
        default:
          throw new Error('スクール認証に失敗しました。');
      }

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('refreshToken', response.data.refreshToken);

      commit('login_success');
    }).catch((error) => {
      throw new Error('スクール認証に失敗しました。');
    });
  },
};

const mapMutations = {
  login(state: State) {
    state.pending = true;
  },
  login_success(state: State) {
    state.loggedIn = true;
    state.pending = false;
    state.token = localStorage.getItem('token');
    state.refreshToken = localStorage.getItem('refreshToken');
    state.latestSchoolId = localStorage.getItem('latestSchoolId');
  },
  login_error(state: State) {
    state.loggedIn = false;
    state.pending = false;
    state.token = null;
    state.refreshToken = null;
  },
  logout(state: State) {
    state.loggedIn = false;
    state.token = null;
    state.refreshToken = null;
  }
};

export default {
  namespaced: true,
  state: mapState,
  getters: mapGetters,
  actions: mapActions,
  mutations: mapMutations,
};
